import { useNostr } from './useNostr';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from './useToast';

/**
 * Hook to manage following/unfollowing a user via NIP-02 kind 3 contact list
 */
export function useFollow(targetPubkey: string) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { mutate: publish } = useNostrPublish();
  const queryClient = useQueryClient();

  // Fetch current user's follow list to determine if we're already following target
  const { data: isFollowing = false, isLoading } = useQuery({
    queryKey: ['is-following', user?.pubkey, targetPubkey],
    queryFn: async () => {
      if (!nostr || !user) return false;
      
      // Get the current user's kind 3 contact list
      const events = await nostr.query([{ kinds: [3], authors: [user.pubkey], limit: 1 }]);
      if (events[0]) {
        return events[0].tags.some(t => t[0] === 'p' && t[1] === targetPubkey);
      }
      return false;
    },
    enabled: !!nostr && !!user && !!targetPubkey,
  });

  // Toggle follow/unfollow
  const toggleFollow = useMutation({
    mutationFn: async () => {
      if (!nostr || !user) throw new Error('Must be logged in to follow');
      
      // Get current follow list
      const events = await nostr.query([{ kinds: [3], authors: [user.pubkey], limit: 1 }]);
      let currentTags = events[0]?.tags ?? [];
      
      // Filter out existing p tags for this target
      const filteredTags = currentTags.filter(t => !(t[0] === 'p' && t[1] === targetPubkey));
      
      // Check if we're currently following
      const wasFollowing = currentTags.some(t => t[0] === 'p' && t[1] === targetPubkey);
      
      // If we were following, remove the tag; otherwise add it
      const newTags = wasFollowing 
        ? filteredTags
        : [...filteredTags, ['p', targetPubkey]];
      
      // Publish updated kind 3 contact list
      const event = await user.signer.signEvent({
        kind: 3,
        content: '',
        tags: newTags,
        created_at: Math.floor(Date.now() / 1000),
      });
      
      await nostr.event(event, { signal: AbortSignal.timeout(5000) });
      
      return { isFollowing: !wasFollowing };
    },
    onSuccess: (result) => {
      const action = result.isFollowing ? 'followed' : 'unfollowed';
      toast({ title: `Successfully ${action}!`, description: '' });
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['following-count', result.isFollowing ? 'self' : 'target'] });
      queryClient.invalidateQueries({ queryKey: ['followers-count', 'target'] });
      queryClient.invalidateQueries({ queryKey: ['is-following', 'target'] });
    },
    onError: (error) => {
      toast({ 
        title: 'Follow action failed', 
        description: error.message,
        variant: 'destructive' 
      });
    },
  });

  return {
    isFollowing,
    isLoading,
    toggleFollow: toggleFollow.mutate,
    isToggling: toggleFollow.isPending,
  };
}